import React from 'react';
import './HedgingStatus.css';
import { getExtendedStats, formatPnL, formatFundingPnL } from '../utils/stats';

function HedgingStatus({ analysis }) {
  if (!analysis) return null;

  const positions = [];
  if (analysis.binance_position) positions.push({ ...analysis.binance_position, exchange: 'Binance' });
  if (analysis.bybit_position) positions.push({ ...analysis.bybit_position, exchange: 'Bybit' });
  if (analysis.okx_position) positions.push({ ...analysis.okx_position, exchange: 'OKX' });
  if (analysis.hyperliquid_position) positions.push({ ...analysis.hyperliquid_position, exchange: 'Hyperliquid' });
  if (analysis.lighter_position) positions.push({ ...analysis.lighter_position, exchange: 'Lighter' });
  if (analysis.extended_position) positions.push({ ...analysis.extended_position, exchange: 'Extended' });

  const stats = getExtendedStats(positions);

  const statusClass = {
    'fully_hedged': 'fully-hedged',
    'partially_hedged': 'partially-hedged',
    'unhedged': 'unhedged'
  }[analysis.status] || 'unhedged';

  const statusLabel = {
    'fully_hedged': 'Fully Hedged',
    'partially_hedged': 'Partially Hedged',
    'unhedged': 'Unhedged'
  }[analysis.status] || 'Unknown';

  // Get position value for visual bars
  const getPositionValue = (position) => {
    if (!position) return 0;
    return position.notional || (position.size * position.mark_price) || 0;
  };

  // Calculate max value for proportional bars
  const getMaxValue = (positions) => {
    if (positions.length === 0) return 1;
    return Math.max(...positions.map(p => getPositionValue(p)), 1);
  };

  const maxValue = getMaxValue(positions);

  // Calculate yield using the same funding-based calculation as summary cards
  // Use fundingPercentYearly from stats (based on actual funding payments, annualized)
  const yieldValue = stats?.fundingPercentYearly ?? 0;

  return (
    <div className={`hedging-status ${statusClass}`}>
      <div className="hedging-header">
        <h4>{analysis.pair}</h4>
        <span className={`status-badge ${statusClass}`}>
          {statusLabel}
        </span>
      </div>
      
      {/* Prominent Arbitrage Yield Display */}
      <div className={`arbitrage-yield-section ${yieldValue >= 0 ? 'positive' : 'negative'}`}>
        <div className="arbitrage-yield-label">Arb Yearly Yield</div>
        <div className={`arbitrage-yield-value ${yieldValue >= 0 ? 'positive' : 'negative'}`}>
          {yieldValue >= 0 ? '+' : ''}{yieldValue.toFixed(2)}%
        </div>
              </div>
      
      {/* Position Breakdown */}
      <div className="position-breakdown">
        {positions.length === 2 ? (
          // Two positions: thin bar with % long (green) / % short (red); exchange pills and values above bar
          (() => {
            const totalValue = positions.reduce((sum, p) => sum + getPositionValue(p), 0);
            return (
              <div className="position-item unified-bar-item">
                <div className="unified-bar-labels">
                  {positions.map((pos, posIndex) => (
                    <span key={posIndex} className="bar-label exchange-pill">
                      {pos.exchange}
                    </span>
                  ))}
                </div>
                <div className="bar-values-row">
                  {positions.map((pos, posIndex) => {
                    const value = getPositionValue(pos);
                    const pct = totalValue > 0 ? (value / totalValue) * 100 : 50;
                    return (
                      <div key={posIndex} className="bar-value-cell" style={{ width: `${pct}%` }}>
                        ${value.toFixed(0)}
                      </div>
                    );
                  })}
                </div>
                <div className="unified-bar-container unified-bar-thin">
                  {positions.map((pos, posIndex) => {
                    const value = getPositionValue(pos);
                    const barWidth = totalValue > 0 ? (value / totalValue) * 100 : 50;
                    const isLong = pos.side === 'LONG';
                    const barColor = isLong ? '#10b981' : '#ef4444';
                    return (
                      <div
                        key={posIndex}
                        className="unified-bar-segment"
                        style={{ width: `${barWidth}%`, backgroundColor: barColor }}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })()
        ) : (
          // Single position: same structure as two-position for aligned spacing and bar (label row → values row → thin bar)
          positions.map((pos, posIndex) => {
            const value = getPositionValue(pos);
            const barWidth = maxValue > 0 ? (value / maxValue) * 100 : 0;
            const isLong = pos.side === 'LONG';
            const barColor = isLong ? '#10b981' : '#ef4444';

            return (
              <div key={posIndex} className="position-item unified-bar-item">
                <div className="unified-bar-labels">
                  <span className="bar-label exchange-pill">{pos.exchange}:</span>
                </div>
                <div className="bar-values-row">
                  <div className="bar-value-cell bar-value-cell-single">${value.toFixed(2)}</div>
                </div>
                <div className="unified-bar-container unified-bar-thin">
                  <div
                    className="unified-bar-segment"
                    style={{ width: `${barWidth}%`, backgroundColor: barColor }}
                  />
                </div>
              </div>
            );
          })
            )}
      </div>
        
      {/* Metrics */}
        <div className="hedging-metrics">
        <div className="metric-item">
          <div className="metric-label">Net Exposure</div>
          <div className={`metric-value ${analysis.net_exposure >= 0 ? 'positive' : 'negative'}`}>
            {formatPnL(analysis.net_exposure)}
          </div>
        </div>
        <div className="metric-item">
          <div className="metric-label">Hedge Ratio</div>
          <div className="metric-value">
              {(analysis.hedge_ratio * 100).toFixed(1)}%
          </div>
        </div>
        <div className="metric-item">
          <div className="metric-label">Agg. PnL</div>
          <div className={`metric-value ${analysis.aggregated_pnl >= 0 ? 'positive' : 'negative'}`}>
            {formatPnL(analysis.aggregated_pnl)}
          </div>
        </div>
        <div className="metric-item">
          <div className="metric-label">Total PnL</div>
          <div className={`metric-value ${(stats?.totalPnL || 0) >= 0 ? 'positive' : 'negative'}`}>
              {formatPnL(stats?.totalPnL || 0)}
          </div>
        </div>
        <div className="metric-item">
          <div className="metric-label">Agg. Funding</div>
          <div className={`metric-value ${stats?.totalFunding >= 0 ? 'positive' : 'negative'}`}>
              {formatFundingPnL(stats?.totalFunding || 0)} ({stats?.fundingPercentPeriod >= 0 ? '+' : ''}{(stats?.fundingPercentPeriod || 0).toFixed(3)}%)
          </div>
        </div>
      </div>
    </div>
  );
}

export default HedgingStatus;

